import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  ZERO_CASH,
  differenceKind,
  differenceOf,
  parseCashAmount,
} from '@aeris/cash'
import type { CashSessionSummary } from '@aeris/contracts'
import {
  MONEY_SCALE,
  formatMoney,
  parseHumanAmount,
  toString,
} from '@aeris/money'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  MoneyInput,
  Textarea,
  notify,
} from '@aeris/ui'

import { ApiError, closeCashSession } from '../lib/api.ts'

/**
 * Fechamento de caixa.
 *
 * The operator types ONE number: what they counted. The expected figure is
 * shown, never editable, and the difference appears as they type — computed by
 * the same `differenceOf` the database's generated column reproduces, so the
 * preview and the record cannot disagree.
 *
 * **A difference never blocks the button.** Closing R$ 40,00 short succeeds and
 * is recorded. A till that refused to close until the cash balanced would be a
 * till whose operator found the missing note in their pocket, and the
 * discrepancy would never be recorded — which is precisely the failure the
 * fechamento exists to prevent. Section 18 asks for the difference to be
 * supported; section 29 asks for it to be recorded. Neither asks for it to be
 * prevented.
 */

export type CashCloseFormProps = {
  session: CashSessionSummary
  /** True when the caller holds `pos.supervise` and this is not their till. */
  supervising: boolean
  onClose: () => void
}

export const CashCloseForm = ({
  session,
  supervising,
  onClose,
}: CashCloseFormProps): ReactNode => {
  const queryClient = useQueryClient()

  const [counted, setCounted] = useState('')
  const [notes, setNotes] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const expected = parseCashAmount(session.expected) ?? ZERO_CASH

  const typed =
    counted.trim().length === 0 ? null : parseHumanAmount(counted, MONEY_SCALE)

  const difference = typed === null ? null : differenceOf(typed, expected)
  const kind = difference === null ? null : differenceKind(difference)

  const countedError = ((): string | undefined => {
    if (counted.trim().length === 0) return undefined
    if (typed === null) return 'Informe um valor. Use vírgula para os centavos.'
    // A drawer cannot hold negative cash. The DIFFERENCE may be negative, and
    // that is the whole point of the screen.
    if (typed.units < 0n) return 'O valor contado não pode ser negativo.'
    return undefined
  })()

  const mutation = useMutation({
    mutationFn: async () => {
      if (typed === null) throw new Error('unreachable: guarded by onSubmit')

      return await closeCashSession(session.id, {
        // The ONLY monetary field. There is no expected amount to send.
        countedAmount: toString(typed),
        notes: notes.trim().length === 0 ? null : notes.trim(),
        version: session.version,
      })
    },
    onSuccess: async () => {
      /*
       * O resultado do fechamento à vista (F248). O diálogo fechava e a tela
       * voltava para "Abrir o caixa": quem fechou não via de novo quanto era
       * esperado, quanto contou e a diferença.
       */
      if (typed !== null && difference !== null) {
        const gap =
          kind === 'exact'
            ? 'o caixa bateu.'
            : kind === 'shortfall'
              ? `faltou ${formatMoney({ units: -difference.units, scale: difference.scale })}.`
              : `sobrou ${formatMoney(difference)}.`
        notify.success(
          `Caixa fechado. Esperado ${formatMoney(expected)}, contado ${formatMoney(typed)}: ${gap}`,
          { duration: 12_000 }
        )
      }
      await queryClient.invalidateQueries({ queryKey: ['cash-session'] })
      await queryClient.invalidateQueries({ queryKey: ['cash-movements'] })
      /*
       * Closed as soon as the server accepted, and the lists refreshed after.
       *
       * It used to await `invalidateQueries` first, which awaits the refetch of
       * every active query it touches — so the dialog stayed open, with its
       * Salvar button spinning, for as long as an unrelated list screen took to
       * reload. The person's work was finished at the 201; making them watch a
       * background refresh is the dialog reporting somebody else's latency as
       * its own.
       */
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['cash-sessions'] })
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível fechar o caixa. Tente novamente.'
      )
    },
  })

  const onSubmit = (): void => {
    setFormError(null)
    if (typed === null) {
      setFormError('Informe o valor contado.')
      return
    }
    if (countedError !== undefined) return
    mutation.mutate()
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Fechar o caixa"
        description={
          supervising
            ? `Você está fechando o caixa de ${session.openedByName ?? 'outro operador'}. O fechamento fica registrado com o seu nome.`
            : 'Conte o dinheiro na gaveta e informe o total. A diferença é calculada e registrada — ela não impede o fechamento.'
        }
        className="max-w-md"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit()
          }}
          noValidate
          className="flex flex-col gap-4"
        >
          {formError === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {formError}
            </p>
          )}

          {/*
            Shown, never editable, and there is no request field it could be
            typed into. A till where somebody can type the expected figure is a
            till that never has a difference.
          */}
          <div className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) px-3 py-2">
            <p className="text-(length:--text-xs) text-(--text-secondary)">
              Esperado pelo sistema — calculado a partir dos{' '}
              {session.movementCount} movimentos
            </p>
            <p className="font-(--font-mono) text-(length:--text-lg) font-(--weight-semibold) text-(--text-primary)">
              {formatMoney(expected)}
            </p>
          </div>

          <Field
            label="Valor contado na gaveta"
            required
            error={countedError}
            hint="Use vírgula para os centavos"
          >
            {(field) => (
              <MoneyInput
                {...field}
                value={counted}
                onValueChange={setCounted}
                autoFocus
              />
            )}
          </Field>

          {/*
            Announced as it changes, so the difference is heard and not only
            seen — this is the number the whole screen exists to produce.
          */}
          <p
            aria-live="polite"
            className="rounded-(--radius-md) px-3 py-2 text-(length:--text-sm)"
          >
            {difference === null ? (
              <span className="text-(--text-muted)">
                A diferença aparece assim que você informar o valor contado.
              </span>
            ) : kind === 'exact' ? (
              <span className="text-(--text-primary)">
                Sem diferença. O caixa bateu.
              </span>
            ) : kind === 'shortfall' ? (
              <span className="font-(--weight-medium) text-(--danger)">
                Falta{' '}
                {formatMoney({
                  units: -difference.units,
                  scale: difference.scale,
                })}
                . O fechamento será registrado com essa diferença.
              </span>
            ) : (
              <span className="font-(--weight-medium) text-(--warning)">
                Sobra {formatMoney(difference)}. O fechamento será registrado
                com essa diferença.
              </span>
            )}
          </p>

          <Field label="Observação" hint="Opcional. Ex.: conferido duas vezes">
            {(field) => (
              <Textarea
                {...field}
                rows={2}
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value)
                }}
              />
            )}
          </Field>

          <DialogFooter>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancelar
            </Button>
            {/*
              Never disabled because of a difference. Only an unparseable or
              negative counted value stops the submission.
            */}
            <Button
              type="submit"
              disabled={mutation.isPending || countedError !== undefined}
            >
              Fechar o caixa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
